import { useState, useEffect, useCallback, useRef } from 'react'
import debounce from 'lodash.debounce'
import lambdaFetch from '../functions/FetchFromLambda'

// API configuration
const API_CONFIG = {
  path: 'opensearch',
  method: 'post',
  defaultDebounceMs: 600
}

/**
 * Custom hook for managing search state and API calls
 */
const useOpenSearch = ({
  page,
  entity,
  searchFields,
  accessToken,
  credentials,
  debounceMs = API_CONFIG.defaultDebounceMs,
  filters = {},
  clearTrigger = false
}) => {
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [selectedValue, setSelectedValue] = useState(null)
  const [touched, setTouched] = useState(false)
  const [isIndexNotFound, setIsIndexNotFound] = useState(false)
  
  const abortControllerRef = useRef(null)
  const searchCache = useRef(new Map())
  const previousFiltersRef = useRef(JSON.stringify(filters))

  // Function to clear cache entries that are no longer relevant
  const clearStaleCache = useCallback(() => {
    const currentKeyPrefix = `${page}:${entity}:`
    const keysToDelete = []
    
    // Find all cache keys that don't match the current page/entity combination
    for (const key of searchCache.current.keys()) {
      if (!key.startsWith(currentKeyPrefix)) {
        keysToDelete.push(key)
      }
    }
    
    // Remove stale entries
    keysToDelete.forEach(key => searchCache.current.delete(key))
  }, [page, entity])

  // Function to clear cache when filters change
  const clearCacheOnFilterChange = useCallback(() => {
    // Clean filters to normalize null/undefined/empty values for comparison
    const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value
      }
      return acc
    }, {})
    const currentFilters = JSON.stringify(cleanFilters)
    const previousFilters = previousFiltersRef.current
    
    // If filters have changed, clear all cache entries for this hook instance
    if (currentFilters !== previousFilters) {
      searchCache.current.clear()
      setOptions([]) // Clear current options to force fresh data
      previousFiltersRef.current = currentFilters
    }
  }, [filters])

  const searchItems = useCallback(async (searchTerm = '') => {
    try {
      // Filter out null, undefined, and empty string values from filters
      // This ensures consistent cache keys and prevents sending invalid filter values to the API
      const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value != null && value !== '') {
          acc[key] = value
        }
        return acc
      }, {})
      
      // Create a comprehensive cache key that includes all relevant parameters
      // Use cleanFilters for cache key to ensure filters with null values use the same cache as filters without null values
      const cacheKey = `${page}:${entity}:${searchTerm}:${JSON.stringify(cleanFilters)}:${JSON.stringify(searchFields)}`
      
      // Check cache first
      if (searchCache.current.has(cacheKey)) {
        setOptions(searchCache.current.get(cacheKey))
        return
      }

      // Cancel any in-flight requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      setLoading(true)

      // Build request body
      // filters: exact match filters (AND logic)
      // searchData: fuzzy/relevance search fields
      const requestBody = {
        page,
        entity,
        filters: cleanFilters,  // Exact match filters (e.g., COMPANY: "ABC") - null/undefined/empty values removed
        searchData: {}  // Fuzzy search fields (e.g., DISPLAY: userInput)
      }

      // Add search term to searchData fields if provided
      if (searchTerm) {
        searchFields.forEach(field => {
          if (field) {  // Only add non-empty fields
            requestBody.searchData[field] = searchTerm
          }
        })
      }

      const response = await lambdaFetch(
        API_CONFIG.path,
        API_CONFIG.method,
        accessToken,
        JSON.stringify(requestBody),
        '',
        credentials
      )

      // Check for index_not_found_exception error
      let errorMessage = null
      if (response === false) {
        // lambdaFetch returns false on error (non-index_not_found_exception errors)
        setIsIndexNotFound(false) // Reset flag
        setOptions([])
        return
      }

      // Check if response has an error field (could be in different locations)
      // This handles both direct error responses and nested error structures
      if (response?.error) {
        errorMessage = response.error
      } else if (response?.data?.error) {
        errorMessage = response.data.error
      } else if (response?.data?.response?.body) {
        try {
          const parsedBody = typeof response.data.response.body === 'string' 
            ? JSON.parse(response.data.response.body) 
            : response.data.response.body
          if (parsedBody?.error) {
            errorMessage = parsedBody.error
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }

      // Check if it's an index_not_found_exception
      if (errorMessage && typeof errorMessage === 'string' && errorMessage.includes('index_not_found_exception')) {
        console.error('[OpenSearch] Index not found error:', errorMessage)
        setIsIndexNotFound(true)
        setOptions([])
        // Don't cache this error state
        return
      }

      // Reset the flag if we got a valid response
      setIsIndexNotFound(false)

      // Parse the response structure
      let items = []
      if (response?.items) {
        items = response.items
      } else if (response?.data?.items) {
        items = response.data.items
      } else if (response?.data) {
        items = Array.isArray(response.data) ? response.data : []
      } else {
        console.warn('No items found in response:', response)
      }
      
      // Cache the results with the comprehensive key
      searchCache.current.set(cacheKey, items)
      setOptions(items)

    } catch (error) {
      if (error.name === 'AbortError') return
      
      // Check if error message contains index_not_found_exception
      const errorMessage = error.message || error.toString()
      if (errorMessage.includes('index_not_found_exception')) {
        console.error('[OpenSearch] Index not found error:', errorMessage)
        setIsIndexNotFound(true)
        setOptions([])
      } else {
        console.error('Search failed:', error)
        setIsIndexNotFound(false)
        setOptions([])
      }
    } finally {
      setLoading(false)
    }
  }, [page, entity, searchFields, accessToken, credentials, filters])

  // Clear stale cache when page or entity changes
  useEffect(() => {
    clearStaleCache()
  }, [clearStaleCache])

  // Clear cache when filters change
  useEffect(() => {
    clearCacheOnFilterChange()
  }, [clearCacheOnFilterChange])

  // Clear dropdown when clearTrigger changes
  useEffect(() => {
    if (clearTrigger) {
      setSelectedValue(null)
      setInputValue('')
      setTouched(false)
      setIsIndexNotFound(false) // Reset index not found flag
    }
  }, [clearTrigger])

  // Cleanup function
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      searchCache.current.clear()
    }
  }, [])

  const debouncedSearch = useCallback(
    debounce((term) => {
      searchItems(term)
    }, debounceMs),
    [searchItems, debounceMs]
  )

  const handleInputChange = useCallback((newInputValue) => {
    setInputValue(newInputValue)
    debouncedSearch(newInputValue.trim())
  }, [debouncedSearch])

  const handleChange = useCallback((newValue, onChange) => {
    setSelectedValue(newValue)
    setTouched(true)
    if (onChange) {
      onChange(newValue)
    }
    if (!newValue) {
      searchItems('')
    }
  }, [searchItems])

  const handleOpen = useCallback(() => {
    if (options.length === 0 && !loading) {
      searchItems('')
    }
  }, [options.length, loading, searchItems])

  const handleBlur = useCallback(() => {
    // Only mark as touched when user leaves the field without selecting
    if (!selectedValue) {
      setTouched(true)
    }
  }, [selectedValue])

  const resetTouched = useCallback(() => {
    setTouched(false)
  }, [])

  const setValue = useCallback((value) => {
    setSelectedValue(value)
  }, [])

  const clearCache = useCallback(() => {
    searchCache.current.clear()
    setOptions([]) // Also clear current options
  }, [])

  const refreshSearch = useCallback(() => {
    clearCache()
    searchItems(inputValue) // Re-run search with current input value
  }, [clearCache, searchItems, inputValue])

  return {
    // State
    loading,
    options,
    inputValue,
    selectedValue,
    touched,
    isIndexNotFound,
    
    // Methods
    searchItems,
    handleInputChange,
    handleChange,
    handleOpen,
    handleBlur,
    resetTouched,
    setValue,
    clearCache,
    refreshSearch
  }
}

export default useOpenSearch
